import { ReactNode } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { classNames } from "primereact/utils";

export interface LoadTableColumn<T> {
    key: string;
    header: string;
    align?: 'left' | 'center' | 'right';
    /** Clase de ancho de la columna (nv-col-*). */
    className?: string;
    /** Contenido de la columna en la fila de alta. */
    addCell?: ReactNode;
    body: (row: T) => ReactNode;
}

export interface LoadTableRow {
    rowKey: string;
}

interface LoadTableProps<T extends LoadTableRow> {
    rows: T[];
    columns: LoadTableColumn<T>[];
    /** Botón ⊕ al final de la fila de alta. */
    addAction: ReactNode;
    rowActions: (row: T) => ReactNode;
    emptyMessage: string;
    /** Texto de la primera celda del pie. */
    footerLabel?: string;
    /** Valores del pie por clave de columna (subtotal, totales). Sin valores, la tabla no lleva pie (input table). */
    footerValues?: Record<string, ReactNode>;
    /** Fila que se está editando en la fila de alta: se marca con `nv-row-editing` (input table). */
    editingRowKey?: string | null;
    /** Sin fila de alta (ej. el perfil no puede editar): la tabla queda de solo lectura. */
    showAddRow?: boolean;
    /** Para miles de filas: solo se dibujan las visibles. Las filas tienen que medir 44px fijos (texto truncado). */
    virtualScroll?: boolean;
    /** Alto de la zona con scroll; por defecto 580px. */
    scrollHeight?: string;
}

const ROW_HEIGHT_PX = 44;

/**
 * Tabla de carga e input table del design system Novarium: la primera fila es la de alta (y edición) y, si hay
 * subtotal, va al pie. Tiene alto máximo y scroll interno; cabecera, fila de alta y pie quedan fijos.
 */
export default function LoadTable<T extends LoadTableRow>({ rows, columns, addAction, rowActions, emptyMessage, footerLabel = 'Subtotal', footerValues, editingRowKey, showAddRow = true, virtualScroll = false, scrollHeight = '580px' }: LoadTableProps<T>) {
    const header = (
        <ColumnGroup>
            <Row>
                {columns.map(c => <Column key={c.key} header={c.header} alignHeader={c.align} headerClassName={c.className} />)}
                <Column header={<span className="nv-sr-only">Acciones</span>} headerClassName="nv-col-actions" />
            </Row>
            {showAddRow && (
                <Row>
                    {columns.map(c => <Column key={`add-${c.key}`} header={c.addCell ?? null} alignHeader={c.align} headerClassName={classNames('nv-add-cell', c.className)} />)}
                    <Column header={addAction} headerClassName="nv-add-cell nv-col-actions" />
                </Row>
            )}
        </ColumnGroup>
    );

    const footer = footerValues && (
        <ColumnGroup>
            <Row>
                {columns.map((c, i) => (
                    <Column
                        key={`footer-${c.key}`}
                        footer={i === 0 ? footerLabel : footerValues[c.key] ?? null}
                        align={c.align}
                        className={c.className}
                    />
                ))}
                <Column footer={null} />
            </Row>
        </ColumnGroup>
    );

    return (
        <DataTable
            value={rows}
            dataKey="rowKey"
            scrollable
            scrollHeight={scrollHeight}
            {...(virtualScroll ? { virtualScrollerOptions: { itemSize: ROW_HEIGHT_PX } } : {})}
            headerColumnGroup={header}
            footerColumnGroup={footer}
            emptyMessage={emptyMessage}
            className="nv-load-table"
            rowClassName={(row: T) => ({ 'nv-row-editing': editingRowKey != null && row.rowKey === editingRowKey })}
        >
            {columns.map(c => <Column key={c.key} body={c.body} align={c.align} className={c.className} />)}
            <Column body={rowActions} className="nv-col-actions" />
        </DataTable>
    );
}
